/**
 * A file, read into the corpus dialog's textarea — and no further.
 *
 * This is a READER, not an importer. It returns text and, for a PDF, the
 * arithmetic of what extracting it cost; it never touches `localStorage` and never
 * calls `addDoc`. The dialog fills its fields with what comes back, the person
 * reads and edits it, and the existing Add button is still the only thing that
 * admits anything. That ordering is the whole argument for allowing PDFs at all:
 * the placeholder's old objection was to a SILENTLY mangled document, and a
 * document sitting in a textarea under a printed count of what it lost, waiting
 * for a press, is not silent.
 *
 * The title is the FILENAME AS GIVEN, extension included. That is deliberate and
 * it repairs something: `docExt` in `Composer.tsx` derives the sources popover's
 * file badge from a trailing extension on the title, and until there was a file
 * input it fell back to a generic `DOC` for nearly everything, because a title
 * somebody typed rarely carries one. A document that came from `paper.pdf` now
 * earns the `PDF` chip honestly — the badge finally names a real format instead of
 * guessing at one.
 */

// The character ceiling `addDoc` refuses at, imported rather than copied: the byte
// bound below is DERIVED from it, so raising one raises the other and the read cap
// keeps saying the arithmetic it prints instead of drifting into a number of its own.
// A second copy of the number would be free to drift, and D13's lesson is that the
// weaker copy is the one somebody reaches. This is a value import and therefore a real runtime edge — the
// only one this module has, it adds nothing to any bundle that did not already carry
// `corpus.ts`, and it does not put a write within reach: `addDoc` is not imported and
// this file still stores nothing.
import { DOC_CHARS } from './corpus'
// TYPE-ONLY, so `verbatimModuleSyntax` erases the line: the pdf module and the
// library behind it must be reachable from the `.pdf` arm's `import()` and from
// nowhere else.
import type { PdfPage } from './pdfText'

/** What the file picker filters on and what the refusal sentence lists, from one
 *  place — two copies would be free to drift, and the drift a user would meet is a
 *  picker that offers a format the reader then refuses. */
export const ACCEPTED = ['.txt', '.md', '.markdown', '.pdf'] as const

/**
 * The largest text or Markdown file this build will read, in bytes.
 *
 * The arithmetic: UTF-8's widest character is 4 bytes, so no document of `DOC_CHARS`
 * characters weighs more than `DOC_CHARS × 4` — which makes this the weight of the
 * heaviest thing the corpus could hold, and so the most that is worth pulling into a
 * tab to find out whether it fits. A READ CAP, and not a theorem about the ceiling
 * downstream: `addDoc` measures after a trim and a newline normalisation, so a file
 * past this bound whose bulk is whitespace at its ends could still have normalised
 * under `DOC_CHARS`. It is refused here for its SIZE, and the gate's sentence says
 * that rather than claiming the character ceiling would have refused it too.
 *
 * Deliberately LOOSE, and the looseness is the point. A file between the ceiling and
 * this bound still buffers, still reaches `addDoc`, and still takes `addDoc`'s refusal
 * with `addDoc`'s arithmetic — because there is one authority on the character
 * question and it is not this file (D13). This gate asks only the question that can be
 * answered before a byte is read, which is how large the input is.
 */
export const TEXT_FILE_BYTES = DOC_CHARS * 4

/**
 * The largest PDF worth opening, in bytes.
 *
 * Not derived from anything, because nothing downstream measures a PDF in bytes — it
 * is an input cap on the parser, and it is named so the number and its reasoning sit
 * together. 50 MB is far beyond any text-bearing paper: a text layer is characters,
 * and a document whose characters would already blow the corpus ceiling many times
 * over is a few megabytes. What is actually that large is a scan — and a scan is
 * images, and images extract to nothing here, because there is no OCR and there will
 * not be one (D35). So the honest answer to a 200 MB PDF is already "no text came out
 * of it"; refusing before `arrayBuffer()` is the same answer sooner, without pulling
 * 200 MB into the tab and running pdf.js over every page of it to arrive there.
 */
export const PDF_FILE_BYTES = 50_000_000

export type SourceFileRead =
  | { kind: 'text' | 'markdown'; title: string; text: string }
  | { kind: 'pdf'; title: string; text: string; pages: PdfPage[]; note: string }
  | { refused: string }

const extOf = (name: string): string =>
  (/\.([a-z0-9]{1,8})$/i.exec(name.trim())?.[1] ?? '').toLowerCase()

/** A file with no name is still a thing that was dropped, and the sentence about
 *  it should not open with a pair of empty quotation marks. */
const named = (name: string): string => (name ? `“${name}”` : 'That file')

const accepted = `${ACCEPTED.slice(0, -1).join(', ')} and ${ACCEPTED[ACCEPTED.length - 1]}`

/**
 * The pdf module, fetched on demand — and the one seam a test can stand in front of.
 *
 * Default-bound to the real dynamic import, which is what keeps the lazy half of
 * the arrangement true: the expression below is still an `import()` evaluated
 * inside a function, so pdf.js stays in its own chunk and somebody who never opens
 * a PDF never downloads it. The indirection buys one thing — a way to make the
 * LOAD fail on purpose, which is the case the arm below now answers and which
 * nothing else can produce, since faking it otherwise means faking module
 * resolution itself. The precedent is `setArchiveBackend` and `setArchiveOpener` in
 * `archive.ts`: a swappable default, tests only, nothing in the app calls it.
 *
 * `typeof import(...)` is a TYPE position, so `verbatimModuleSyntax` erases it and
 * the header's rule holds — the module is reachable at runtime from the loader
 * below and from nowhere else.
 */
type PdfModule = typeof import('./pdfText')
let loadPdf: () => Promise<PdfModule> = () => import('./pdfText')

/** Swap the loader, or pass null to restore the real import. Tests only. */
export function setPdfLoader(load: (() => Promise<PdfModule>) | null): void {
  loadPdf = load ?? (() => import('./pdfText'))
}

/**
 * Read a file the person chose or dropped.
 *
 * Refuses in one sentence that names what IS accepted, for the same reason
 * `addDoc` refuses with the arithmetic: a bare "unsupported file" leaves somebody
 * guessing at a list they cannot see, and the obvious guess is that the feature is
 * broken.
 *
 * The size questions are asked FIRST, from `file.size` alone and before a byte is
 * held — see the two bounds above. Both are about the tab rather than about the
 * corpus: an input read whole before anybody asks how big it is is an input that can
 * freeze the page on its way to a no. Neither bound claims to know what the gates
 * downstream would have said about the same file; each says what this reads.
 */
export async function readSourceFile(file: File): Promise<SourceFileRead> {
  const title = file.name.trim()
  const ext = extOf(title)

  if (ext === 'txt' || ext === 'md' || ext === 'markdown') {
    // Asked BEFORE the read, and it is the only size question this file asks. What
    // the bound IS: this build's read cap for a text source, `DOC_CHARS × 4` bytes,
    // set above the weight of anything the corpus could hold. What it is NOT: proof
    // that the file would have been refused downstream. It used to say it was —
    // "nothing that size fits under it however it is encoded" — and the counterexample
    // is in the next comment down, which this one has always relied on: `addDoc` trims
    // the ends, so a file whose bulk is whitespace can cross this bound and still
    // normalise under `DOC_CHARS`. That file is refused unread, and the price of
    // learning otherwise is buffering an input of unbounded size to ask a question
    // about its ends — which is the tab freezing on its way to a no. Everything UNDER
    // the bound still gets exactly the corpus arithmetic, because `addDoc` trims and
    // counts the way it always did; nothing about the ceiling moved here.
    if (file.size > TEXT_FILE_BYTES) {
      return {
        refused: `${named(title)} is ${file.size.toLocaleString()} bytes and the most this reads from a text file is ${TEXT_FILE_BYTES.toLocaleString()} — four bytes to the widest UTF-8 character times the ${DOC_CHARS.toLocaleString()} characters the corpus takes. It was refused without being read. Split it, or paste the part that matters.`,
      }
    }
    // Whitespace is preserved exactly as the file carried it. `passagesOf` splits on
    // blank lines, so stripping or collapsing here would silently re-chunk somebody's
    // document on the way in — and `addDoc` trims the ends itself anyway.
    return { kind: ext === 'txt' ? 'text' : 'markdown', title, text: await file.text() }
  }

  if (ext !== 'pdf') {
    return {
      refused: `${named(title)} is not a format this reads. It takes ${accepted} — open anything else and paste the part that matters.`,
    }
  }

  // Before the loader, and so before `arrayBuffer()`: a file this reader will not
  // parse is not worth fetching a 433 kB parser for, let alone buffering into memory
  // first. The refusal names the size and the bound, because "too large" with no
  // numbers is the refusal `addDoc` has always refused to write.
  if (file.size > PDF_FILE_BYTES) {
    return {
      refused: `${named(title)} is ${file.size.toLocaleString()} bytes and the limit is ${PDF_FILE_BYTES.toLocaleString()} bytes. A PDF that large is a scan, and a scan is images with no text layer to extract, so this was refused without being read. Open it and paste the part that matters.`,
    }
  }

  // Lazily, and only from here. pdf.js is by a wide margin the largest thing this
  // app depends on, and somebody who never opens a PDF must never download it.
  //
  // THE FETCH ITSELF CAN FAIL, and it fails for reasons that have nothing to do
  // with the file: a tab left open across a deploy asks for a hashed chunk that no
  // longer exists, and a machine that has gone offline since the page loaded asks
  // for one it cannot reach. This used to be an unguarded `await`, so the rejection
  // left the whole function — and the dialog's handler had no catch either, which
  // made a routine deploy read as a file reader that simply stops. Its own sentence
  // rather than the parse refusal's, because the parse refusal sends the reader off
  // to check their file, and here the file has not been opened at all: the remedy
  // is a reload, and the document is fine.
  let pdf: PdfModule
  try {
    pdf = await loadPdf()
  } catch {
    return {
      refused: `${named(title)} was not read — the PDF reader could not be loaded. Reload the page and try again; the file itself was never opened.`,
    }
  }
  try {
    // The whole extract goes to the note, not just the page rows: the read can stop
    // on either of `pdfText.ts`'s two bounds, and what the document DECLARED and
    // which bound fired live nowhere else. A note built from `pages` alone would
    // print a page count for a truncated read as though it were the document.
    const got = await pdf.extractPdfText(await file.arrayBuffer())
    return {
      kind: 'pdf',
      title,
      text: got.text,
      pages: got.pages,
      note: pdf.extractionNote(got),
    }
  } catch (e) {
    // Encrypted, truncated, or not a PDF behind the extension. pdf.js's own message
    // ("The PDF file is encrypted", "Invalid PDF structure") is the most specific
    // thing anybody has here, and a generic "could not read that" would send the
    // reader off to check the wrong thing.
    const why =
      e instanceof Error && e.message ? e.message.replace(/\.$/, '') : 'it would not parse'
    return {
      refused: `${named(title)} could not be read as a PDF — ${why}. Open it and paste the part that matters.`,
    }
  }
}
